import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Navigation from '@/components/Navigation';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Settings, Plus, Users, Award, UserCog, Mail, FileText } from 'lucide-react';
import { useTournament } from '@/context/TournamentContext';
import SimpleRichEditor from '@/components/SimpleRichEditor';


import { API_BASE_URL } from '@/config/api';
const API = API_BASE_URL;

export default function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [newTournamentYear, setNewTournamentYear] = useState(new Date().getFullYear());
  const [newTournamentName, setNewTournamentName] = useState('');
  const [semi1Region1, setSemi1Region1] = useState('East');
  const [semi1Region2, setSemi1Region2] = useState('West');
  const [semi2Region1, setSemi2Region1] = useState('South');
  const [semi2Region2, setSemi2Region2] = useState('Midwest');
  const [loading, setLoading] = useState(true);
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [entryFee, setEntryFee] = useState('');
  const [paypalUsername, setPaypalUsername] = useState('');
  const [venmoUsername, setVenmoUsername] = useState('');
  const [cashappUsername, setCashappUsername] = useState('');
  const [participants, setParticipants] = useState([]);
  const [showParticipants, setShowParticipants] = useState(false);
  const [games, setGames] = useState([]);
  const [showGames, setShowGames] = useState(false);
  const [teams, setTeams] = useState([]);
  const [selectedRound, setSelectedRound] = useState('all'); // Filter for games by round
  const { currentTournament, selectedTournamentId } = useTournament();
  // Team form state
  const [teamsInput, setTeamsInput] = useState('');
  // Semifinal config editing
  const [editingConfigTournamentId, setEditingConfigTournamentId] = useState(null);
  const [editSemi1Region1, setEditSemi1Region1] = useState('');
  const [editSemi1Region2, setEditSemi1Region2] = useState('');
  const [editSemi2Region1, setEditSemi2Region1] = useState('');
  const [editSemi2Region2, setEditSemi2Region2] = useState('');
  // Email broadcast state
  const [emailSubject, setEmailSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [emailFilter, setEmailFilter] = useState('all');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailTestMode, setEmailTestMode] = useState(false);
  const [testEmails, setTestEmails] = useState('');
  const [emailSentConfirmation, setEmailSentConfirmation] = useState(null);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingUserName, setEditingUserName] = useState('');
  const [showSwapRegions, setShowSwapRegions] = useState(false);
  const [emailLogs, setEmailLogs] = useState([]);
  const [showEmailLogs, setShowEmailLogs] = useState(false);
  const [loadingEmailLogs, setLoadingEmailLogs] = useState(false);
  const [swapRegion1, setSwapRegion1] = useState('');
  const [swapRegion2, setSwapRegion2] = useState('');
  const [swapTeamsR1, setSwapTeamsR1] = useState([]);
  const [swapTeamsR2, setSwapTeamsR2] = useState([]);
  const [swapping, setSwapping] = useState(false);

  useEffect(() => {
    console.log('[AdminDashboard] useEffect triggered. selectedTournamentId:', selectedTournamentId);
    
    const fetchData = async () => {
      if (!selectedTournamentId) {
        console.log('[AdminDashboard] No selectedTournamentId, skipping fetch');
        setLoading(false);
        return;
      }

      console.log('[AdminDashboard] Starting data fetch for tournament:', selectedTournamentId);

      // PRIORITY: Fetch games and teams FIRST (this works for all users)
      try {
        console.log('[AdminDashboard] Fetching bracket data for tournament:', selectedTournamentId);
        const gamesRes = await axios.get(
          `${API}/bracket?tournament_id=${selectedTournamentId}`,
          { withCredentials: false }
        );
        console.log('[AdminDashboard] Bracket data received:', {
          teams: gamesRes.data.teams?.length || 0,
          games: gamesRes.data.games?.length || 0
        });
        setGames(gamesRes.data.games || []);
        setTeams(gamesRes.data.teams || []);
        console.log('[AdminDashboard] Games state set to:', gamesRes.data.games?.length || 0);
      } catch (err) {
        console.error('[AdminDashboard] Error fetching bracket data:', err);
        setGames([]);
        setTeams([]);
      }

      // Fetch user data
      try {
        const userRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(userRes.data);
      } catch (error) {
        console.error('Error fetching user:', error);
      }

      // Fetch admin data (tournaments and users)
      try {
        const tournamentsRes = await axios.get(`${API}/admin/tournaments`, {
          withCredentials: true,
        });
        setTournaments(tournamentsRes.data);

        const usersRes = await axios.get(`${API}/admin/users`, {
          withCredentials: true,
        });
        setAllUsers(usersRes.data);
      } catch (error) {
        console.error('Error fetching admin data:', error);
      }

      // Fetch tournament-specific data (participants)
      try {
        const participantsRes = await axios.get(
          `${API}/admin/tournaments/${selectedTournamentId}/participants`,
          { withCredentials: true }
        );
        setParticipants(participantsRes.data);
      } catch (err) {
        console.log('No participants data yet');
      }

      setLoading(false);
    };

    fetchData();
  }, [selectedTournamentId]);

  const handleCreateTournament = async () => {
    try {
      const tournamentName = newTournamentName || newTournamentYear.toString();
      await axios.post(
        `${API}/admin/tournaments?year=${newTournamentYear}&name=${encodeURIComponent(tournamentName)}&semi1_region1=${semi1Region1}&semi1_region2=${semi1Region2}&semi2_region1=${semi2Region1}&semi2_region2=${semi2Region2}`,
        {},
        { withCredentials: true }
      );
      toast.success('Tournament created!');
      window.location.reload();
    } catch (error) {
      toast.error('Failed to create tournament');
    }
  };

  const handleSeedDummyData = async () => {
    const year = new Date().getFullYear();
    
    try {
      const response = await axios.post(
        `${API}/admin/seed-dummy-data?year=${year}`,
        {},
        { withCredentials: true }
      );
      toast.success(`${response.data.tournament_name} created with ${response.data.teams_created} teams and ${response.data.games_created} games!`);
      window.location.reload();
    } catch (error) {
      toast.error('Failed to seed dummy data');
    }
  };

  const handleUpdateStatus = async (tournamentId, status) => {
    try {
      await axios.put(
        `${API}/admin/tournaments/${tournamentId}/status?status=${status}`,
        {},
        { withCredentials: true }
      );
      toast.success('Status updated!');
      window.location.reload();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAddTeams = async () => {
    if (!currentTournament) {
      toast.error('No active tournament');
      return;
    }

    try {
      const lines = teamsInput.split('\n').filter((line) => line.trim());
      const teams = lines.map((line) => {
        const parts = line.split(',').map((p) => p.trim());
        return {
          name: parts[0],
          seed: parseInt(parts[1]),
          region: parts[2],
        };
      });

      await axios.post(
        `${API}/admin/tournaments/${selectedTournamentId}/teams`,
        teams,
        { withCredentials: true }
      );

      // Automatically move tournament to "active" status after adding teams
      if (currentTournament.status === 'setup') {
        await axios.put(
          `${API}/admin/tournaments/${selectedTournamentId}/status?status=active`,
          {},
          { withCredentials: true }
        );
      }

      toast.success(`${teams.length} teams added! Tournament is now active.`);
      setTeamsInput('');
      setShowTeamForm(false);
      window.location.reload(); // Refresh to show updated status
    } catch (error) {
      toast.error('Failed to add teams');
    }
  };

  const handleSwapRegions = async () => {
    if (swapTeamsR1.length === 0 || swapTeamsR2.length === 0) {
      toast.error('Select teams from both regions');
      return;
    }
    setSwapping(true);
    try {
      const res = await axios.post(`${API}/admin/swap-team-regions`, {
        tournament_id: selectedTournamentId,
        team_ids_region_1: swapTeamsR1,
        team_ids_region_2: swapTeamsR2
      }, { withCredentials: true });
      toast.success(res.data.message);
      setSwapTeamsR1([]);
      setSwapTeamsR2([]);
      setSwapRegion1('');
      setSwapRegion2('');
      setShowSwapRegions(false);
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to swap regions');
    } finally {
      setSwapping(false);
    }
  };


  const handlePromoteUser = async (userId) => {
    try {
      await axios.post(
        `${API}/admin/users/${userId}/promote`,
        {},
        { withCredentials: true }
      );
      toast.success('User promoted to admin!');
      // Refresh users list
      const usersRes = await axios.get(`${API}/admin/users`, {
        withCredentials: true,
      });
      setAllUsers(usersRes.data);
    } catch (error) {
      toast.error('Failed to promote user');
    }
  };

  const handleDemoteUser = async (userId) => {
    if (!window.confirm('Remove admin privileges from this user?')) return;

    try {
      await axios.post(
        `${API}/admin/users/${userId}/demote`,
        {},
        { withCredentials: true }
      );
      toast.success('Admin privileges removed');
      // Refresh users list
      const usersRes = await axios.get(`${API}/admin/users`, {
        withCredentials: true,
      });
      setAllUsers(usersRes.data);
    } catch (error) {
      toast.error('Failed to remove admin privileges');
    }
  };

  const handlePromoteSuperAdmin = async (userId) => {
    if (!window.confirm('Promote this user to SuperAdmin? They will have full control over tournament management.')) return;

    try {
      await axios.post(
        `${API}/admin/users/${userId}/promote-superadmin`,
        {},
        { withCredentials: true }
      );
      toast.success('User promoted to SuperAdmin!');
      // Refresh users list
      const usersRes = await axios.get(`${API}/admin/users`, {
        withCredentials: true,
      });
      setAllUsers(usersRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to promote user');
    }
  };

  const handleDemoteSuperAdmin = async (userId) => {
    if (!window.confirm('Remove SuperAdmin privileges from this user?')) return;

    try {
      await axios.post(
        `${API}/admin/users/${userId}/demote-superadmin`,
        {},
        { withCredentials: true }
      );
      toast.success('SuperAdmin privileges removed');
      // Refresh users list
      const usersRes = await axios.get(`${API}/admin/users`, {
        withCredentials: true,
      });
      setAllUsers(usersRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove SuperAdmin privileges');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      toast.success(`User ${userName} deleted successfully`);
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleEditUserName = (userId, currentName) => {
    setEditingUserId(userId);
    setEditingUserName(currentName);
  };

  const handleSaveUserName = async (userId) => {
    if (!editingUserName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }

    try {
      await axios.put(
        `${API}/admin/users/${userId}/name`,
        { name: editingUserName },
        { withCredentials: true }
      );
      toast.success('User name updated successfully!');
      
      // Refresh users list
      const usersRes = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setAllUsers(usersRes.data);
      
      setEditingUserId(null);
      setEditingUserName('');
    } catch (error) {
      toast.error('Failed to update user name');
    }
  };

  const handleCancelEditUserName = () => {
    setEditingUserId(null);
    setEditingUserName('');
  };

  const handleUpdatePaymentInfo = async () => {
    if (!currentTournament) {
      toast.error('No active tournament');
      return;
    }

    try {
      await axios.put(
        `${API}/admin/tournaments/${selectedTournamentId}/payment`,
        {
          entry_fee: entryFee ? parseFloat(entryFee) : null,
          paypal_username: paypalUsername || null,
          venmo_username: venmoUsername || null,
          cashapp_username: cashappUsername || null,
        },
        { withCredentials: true }
      );
      toast.success('Payment info updated!');
      window.location.reload();
    } catch (error) {
      toast.error('Failed to update payment info');
    }
  };

  const handleDeleteTournament = async (tournamentId) => {
    if (!window.confirm('Are you sure you want to DELETE this tournament? This will permanently delete all games, picks, and participant data. This action CANNOT be undone!')) {
      return;
    }

    try {
      await axios.delete(
        `${API}/admin/tournaments/${tournamentId}`,
        { withCredentials: true }
      );
      toast.success('Tournament deleted!');
      window.location.reload();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to delete tournament';
      toast.error(errorMsg);
      console.error('Delete tournament error:', error);
    }
  };

  const handleLockPicks = async (tournamentId, locked) => {
    try {
      await axios.put(
        `${API}/admin/tournaments/${tournamentId}/lock-picks?locked=${locked}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Picks ${locked ? 'locked' : 'unlocked'}!`);
      window.location.reload();
    } catch (error) {
      toast.error('Failed to update picks lock');
    }
  };

  const handleMarkPaid = async (userId, paid) => {
    if (!currentTournament) return;

    try {
      await axios.put(
        `${API}/admin/tournaments/${selectedTournamentId}/participants/${userId}/paid?paid=${paid}`,
        {},
        { withCredentials: true }
      );
      toast.success(`User marked as ${paid ? 'paid' : 'unpaid'}!`);
      
      // Refresh participants
      const participantsRes = await axios.get(
        `${API}/admin/tournaments/${selectedTournamentId}/participants`,
        { withCredentials: true }
      );
      setParticipants(participantsRes.data);
    } catch (error) {
      toast.error('Failed to update payment status');
    }
  };

  const handleConfirmPayment = async (userId) => {
    if (!currentTournament) return;

    try {
      await axios.post(
        `${API}/admin/tournaments/${selectedTournamentId}/confirm-payment/${userId}`,
        {},
        { withCredentials: true }
      );
      toast.success('Payment confirmed!');
      
      // Refresh participants
      const participantsRes = await axios.get(
        `${API}/admin/tournaments/${selectedTournamentId}/participants`,
        { withCredentials: true }
      );
      setParticipants(participantsRes.data);
    } catch (error) {
      toast.error('Failed to confirm payment');
    }
  };

  const handleUnconfirmPayment = async (userId) => {
    if (!currentTournament) return;

    try {
      await axios.post(
        `${API}/admin/tournaments/${selectedTournamentId}/unconfirm-payment/${userId}`,
        {},
        { withCredentials: true }
      );
      toast.success('Payment marked as unpaid');
      
      // Refresh participants
      const participantsRes = await axios.get(
        `${API}/admin/tournaments/${selectedTournamentId}/participants`,
        { withCredentials: true }
      );
      setParticipants(participantsRes.data);
    } catch (error) {
      toast.error('Failed to update payment status');
    }
  };

  const fetchEmailLogs = async () => {
    setLoadingEmailLogs(true);
    try {
      const response = await axios.get(`${API}/admin/email-logs`, { withCredentials: true });
      setEmailLogs(response.data.logs || []);
    } catch (error) {
      toast.error('Failed to load email logs');
    } finally {
      setLoadingEmailLogs(false);
    }
  };

  const handleSendBroadcastEmail = async () => {
    if (!selectedTournamentId) {
      toast.error('Please select a tournament first');
      return;
    }

    if (!emailSubject.trim()) {
      toast.error('Please enter an email subject');
      return;
    }

    if (!emailContent.trim()) {
      toast.error('Please enter email content');
      return;
    }

    // Validate test mode
    if (emailTestMode) {
      if (!testEmails.trim()) {
        toast.error('Please enter at least one test email address');
        return;
      }
      
      // Parse and validate emails
      const emailList = testEmails.split(',').map(e => e.trim()).filter(e => e);
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalidEmails = emailList.filter(e => !emailRegex.test(e));
      
      if (invalidEmails.length > 0) {
        toast.error(`Invalid email addresses: ${invalidEmails.join(', ')}`);
        return;
      }
    }

    setSendingEmail(true);

    try {
      const emailList = emailTestMode 
        ? testEmails.split(',').map(e => e.trim()).filter(e => e)
        : null;

      const response = await axios.post(
        `${API}/admin/send-broadcast-email`,
        {
          tournament_id: selectedTournamentId,
          subject: emailSubject,
          html_content: emailContent,
          filter_type: emailFilter,
          test_mode: emailTestMode,
          test_emails: emailList
        },
        { withCredentials: true }
      );

      const modeText = emailTestMode ? ' (TEST MODE)' : '';
      const confirmationMsg = `✅ Email sent successfully${modeText}! ${response.data.sent} emails delivered.`;
      
      toast.success(confirmationMsg, { duration: 5000 });
      setEmailSentConfirmation(confirmationMsg);
      
      // Clear form
      setEmailSubject('');
      setEmailContent('');
      setTestEmails('');
      setEmailTestMode(false);
      
      // Scroll to top to show success message clearly
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Hide confirmation banner after 8 seconds
      setTimeout(() => setEmailSentConfirmation(null), 8000);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCompleteGame = async (gameId, winnerId) => {
    if (!currentTournament) return;

    try {
      await axios.post(
        `${API}/admin/games/${gameId}/result`,
        { winner_id: winnerId },
        { withCredentials: true }
      );
      toast.success('Game marked as complete!');
      
      // Refresh games and teams
      const gamesRes = await axios.get(
        `${API}/bracket?tournament_id=${selectedTournamentId}`
      );
      setGames(gamesRes.data.games || []);
      setTeams(gamesRes.data.teams || []);
    } catch (error) {
      toast.error('Failed to complete game');
    }
  };

  const handleResetGame = async (gameId) => {
    try {
      console.log('Resetting game:', gameId);
      const response = await axios.delete(
        `${API}/admin/games/${gameId}/result`,
        { withCredentials: true }
      );
      console.log('Reset response:', response);
      toast.success('Game reset!');
      
      // Refresh games and teams
      const gamesRes = await axios.get(
        `${API}/bracket?tournament_id=${selectedTournamentId}`
      );
      setGames(gamesRes.data.games || []);
      setTeams(gamesRes.data.teams || []);
    } catch (error) {
      console.error('Reset error:', error);
      toast.error('Failed to reset game: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleGenerateNextRound = async () => {
    if (!currentTournament) return;

    try {
      const response = await axios.post(
        `${API}/admin/tournaments/${selectedTournamentId}/generate-next-round`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data.message);
      
      // Refresh games
      const gamesRes = await axios.get(
        `${API}/bracket?tournament_id=${selectedTournamentId}`
      );
      setGames(gamesRes.data.games || []);
      setTeams(gamesRes.data.teams || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate next round');
    }
  };

  const handleDeleteRound = async (roundNumber) => {
    if (!window.confirm(`Delete all games in Round ${roundNumber}? This cannot be undone.`)) return;

    try {
      await axios.delete(
        `${API}/admin/tournaments/${selectedTournamentId}/rounds/${roundNumber}`,
        { withCredentials: true }
      );
      toast.success(`Round ${roundNumber} deleted successfully`);
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete round');
    }
  };

  const handlePurgeRoundRegion = async (roundNumber, region) => {
    if (!window.confirm(`Purge Round ${roundNumber} games in ${region}? This will delete those games so you can re-score.`)) return;
    try {
      await axios.delete(
        `${API}/admin/purge-round-games?tournament_id=${selectedTournamentId}&round_number=${roundNumber}&region=${encodeURIComponent(region)}`,
        { withCredentials: true }
      );
      toast.success(`Round ${roundNumber} ${region} games purged`);
      const gamesRes = await axios.get(`${API}/bracket?tournament_id=${selectedTournamentId}`);
      setGames(gamesRes.data.games || []);
      setTeams(gamesRes.data.teams || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to purge games');
    }
  };

  const handleRegenerateRound = async (roundNumber) => {
    if (!window.confirm(`Regenerate Round ${roundNumber}? This will delete existing games and create new matchups.`)) return;

    try {
      const response = await axios.post(
        `${API}/admin/tournaments/${selectedTournamentId}/rounds/${roundNumber}/regenerate`,
        {},
        { withCredentials: true }
      );
      toast.success(`Round ${roundNumber} regenerated! ${response.data.games_created} games created.`);
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to regenerate round');
    }
  };

  const handleEditConfig = (tournament) => {
    setEditingConfigTournamentId(tournament.tournament_id);
    setEditSemi1Region1(tournament.semi1_region1 || 'East');
    setEditSemi1Region2(tournament.semi1_region2 || 'West');
    setEditSemi2Region1(tournament.semi2_region1 || 'South');
    setEditSemi2Region2(tournament.semi2_region2 || 'Midwest');
  };

  const handleSaveConfig = async (tournamentId) => {
    try {
      await axios.put(
        `${API}/admin/tournaments/${tournamentId}/semifinal-config`,
        {
          semi1_region1: editSemi1Region1,
          semi1_region2: editSemi1Region2,
          semi2_region1: editSemi2Region1,
          semi2_region2: editSemi2Region2
        },
        { withCredentials: true }
      );
      toast.success('Semifinal configuration updated!');
      setEditingConfigTournamentId(null);
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update configuration');
    }
  };

  const handleCancelEditConfig = () => {
    setEditingConfigTournamentId(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-amber-600 border-t-transparent"></div>
      </div>
    );
  }

  console.log('[AdminDashboard] RENDER - games.length:', games.length, 'selectedTournamentId:', selectedTournamentId);

  return (
    <div className="min-h-screen bg-stone-50">
      <Navigation user={user} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-emerald-900 rounded-md p-8 mb-8 card-shadow text-white">
          <div className="flex items-center space-x-3">
            <Settings size={48} className="text-amber-600" />
            <div>
              <h1 className="font-bebas text-5xl tracking-wider">Admin Dashboard</h1>
              <p className="font-manrope text-emerald-100">Manage tournaments and settings</p>
            </div>
          </div>
        </div>

        {/* Create Tournament */}
        <div className="bg-white rounded-md card-shadow p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Plus className="text-amber-600" size={24} />
              <h2 className="font-bebas text-3xl text-emerald-900 tracking-wider">
                Create New Tournament
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label htmlFor="tournament-name" className="font-manrope">
                Tournament Name
              </Label>
              <Input
                id="tournament-name"
                data-testid="tournament-name-input"
                type="text"
                placeholder="e.g., March Madness 2026, Spring Championship"
                value={newTournamentName}
                onChange={(e) => setNewTournamentName(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="year" className="font-manrope">
                Year
              </Label>
              <Input
                id="year"
                data-testid="tournament-year-input"
                type="number"
                value={newTournamentYear}
                onChange={(e) => setNewTournamentYear(parseInt(e.target.value))}
                className="mt-1"
              />
            </div>
          </div>

          <div className="bg-emerald-50 p-4 rounded-md mb-4">
            <h3 className="font-bebas text-xl text-emerald-900 mb-3">Final Four Bracket Configuration</h3>
            <p className="text-sm text-gray-600 font-manrope mb-3">
              Set which regions face each other in the semifinals (important for max score calculations)
            </p>
            
            {/* Visual Preview */}
            <div className="bg-white p-4 rounded-md border-2 border-emerald-600 mb-4">
              <h4 className="font-bebas text-lg text-emerald-900 mb-2">📋 Preview: Final Four Matchups</h4>
              <div className="space-y-2 font-manrope">
                <div className="flex items-center justify-between bg-amber-50 p-3 rounded border border-amber-300">
                  <span className="font-bold text-amber-900">Semifinal 1:</span>
                  <span className="text-emerald-900 font-semibold">{semi1Region1} vs {semi1Region2}</span>
                </div>
                <div className="flex items-center justify-between bg-amber-50 p-3 rounded border border-amber-300">
                  <span className="font-bold text-amber-900">Semifinal 2:</span>
                  <span className="text-emerald-900 font-semibold">{semi2Region1} vs {semi2Region2}</span>
                </div>
                <div className="flex items-center justify-center bg-green-50 p-3 rounded border border-green-300 mt-3">
                  <span className="font-bold text-green-900">Championship:</span>
                  <span className="text-emerald-900 font-semibold ml-2">Winner of Semi 1 vs Winner of Semi 2</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white p-3 rounded border border-emerald-200">
                <Label className="font-manrope font-bold text-emerald-900 mb-2 block">Semifinal 1</Label>
                <div className="flex items-center space-x-2">
                  <select
                    value={semi1Region1}
                    onChange={(e) => setSemi1Region1(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md font-manrope"
                  >
                    <option value="East">East</option>
                    <option value="West">West</option>
                    <option value="South">South</option>
                    <option value="Midwest">Midwest</option>
                  </select>
                  <span className="font-bebas text-xl text-gray-500">vs</span>
                  <select
                    value={semi1Region2}
                    onChange={(e) => setSemi1Region2(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md font-manrope"
                  >
                    <option value="East">East</option>
                    <option value="West">West</option>
                    <option value="South">South</option>
                    <option value="Midwest">Midwest</option>
                  </select>
                </div>
              </div>
              <div className="bg-white p-3 rounded border border-emerald-200">
                <Label className="font-manrope font-bold text-emerald-900 mb-2 block">Semifinal 2</Label>
                <div className="flex items-center space-x-2">
                  <select
                    value={semi2Region1}
                    onChange={(e) => setSemi2Region1(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md font-manrope"
                  >
                    <option value="East">East</option>
                    <option value="West">West</option>
                    <option value="South">South</option>
                    <option value="Midwest">Midwest</option>
                  </select>
                  <span className="font-bebas text-xl text-gray-500">vs</span>
                  <select
                    value={semi2Region2}
                    onChange={(e) => setSemi2Region2(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md font-manrope"
                  >
                    <option value="East">East</option>
                    <option value="West">West</option>
                    <option value="South">South</option>
                    <option value="Midwest">Midwest</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Button
              onClick={handleCreateTournament}
              data-testid="create-tournament-button"
              className="btn-primary"
            >
              Create Tournament
            </Button>
            <p className="text-sm text-gray-600 font-manrope">
              Name is optional - will default to year if left blank
            </p>
          </div>
        </div>

        {/* Add Teams */}
        {currentTournament && (
          <>
            <div className="bg-white rounded-md card-shadow p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <Users className="text-amber-600" size={24} />
                  <h2 className="font-bebas text-3xl text-emerald-900 tracking-wider">
                    Add Teams to {currentTournament.name || currentTournament.year} ({currentTournament.year})
                  </h2>
                </div>
                <Button
                  data-testid="toggle-team-form-button"
                  onClick={() => setShowTeamForm(!showTeamForm)}
                  className="btn-secondary px-4 py-2"
                >
                  {showTeamForm ? 'Hide' : 'Show'} Form
                </Button>
              </div>

              {showTeamForm && (
                <div className="space-y-4">
                  <div>
                    <Label className="font-manrope">
                      Teams (Format: Name, Seed, Region - one per line)
                    </Label>
                    <textarea
                      data-testid="teams-input"
                      value={teamsInput}
                      onChange={(e) => setTeamsInput(e.target.value)}
                      placeholder="Duke, 1, East
North Carolina, 2, East
Gonzaga, 1, West"
                      rows={10}
                      className="w-full mt-1 p-3 border border-gray-300 rounded-md font-jetbrains text-sm"
                    />
                  </div>
                  <Button
                    data-testid="add-teams-button"
                    onClick={handleAddTeams}
                    className="btn-primary px-6 py-2"
                  >
                    Add Teams
                  </Button>
                </div>
              )}
            </div>

            {/* Swap Team Regions */}
            {teams.length > 0 && (
              <div className="bg-white rounded-md card-shadow p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl">🔄</span>
                    <h2 className="font-bebas text-3xl text-emerald-900 tracking-wider">
                      Move Teams Between Regions
                    </h2>
                  </div>
                  <Button
                    data-testid="toggle-swap-regions-button"
                    onClick={() => setShowSwapRegions(!showSwapRegions)}
                    className="btn-secondary px-4 py-2"
                  >
                    {showSwapRegions ? 'Hide' : 'Show'}
                  </Button>
                </div>
                <p className="font-manrope text-gray-600 text-sm mb-2">
                  Swap two teams between regions. Updates teams, games, and bracket positions.
                </p>

                {showSwapRegions && (
                  <div className="space-y-4 mt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Region 1 */}
                      <div className="border border-gray-200 rounded-md p-4">
                        <Label className="font-manrope font-semibold mb-2 block">From Region</Label>
                        <select
                          data-testid="swap-from-region"
                          value={swapRegion1}
                          onChange={(e) => { setSwapRegion1(e.target.value); setSwapTeamsR1([]); }}
                          className="w-full p-2 border border-gray-300 rounded-md font-manrope text-sm mb-3"
                        >
                          <option value="">Select region...</option>
                          {['East', 'West', 'South', 'Midwest'].filter(r => r !== swapRegion2).map(r => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                        </select>
                        {swapRegion1 && (
                          <div className="space-y-1 max-h-48 overflow-y-auto">
                            {[...teams].filter(t => t.region === swapRegion1).sort((a, b) => a.seed - b.seed).map(t => (
                              <label key={t.team_id} className="flex items-center gap-2 p-1.5 rounded hover:bg-gray-50 cursor-pointer font-manrope text-sm">
                                <input
                                  type="checkbox"
                                  checked={swapTeamsR1.includes(t.team_id)}
                                  onChange={(e) => {
                                    if (e.target.checked) setSwapTeamsR1(prev => [...prev, t.team_id]);
                                    else setSwapTeamsR1(prev => prev.filter(id => id !== t.team_id));
                                  }}
                                  className="rounded"
                                />
                                ({t.seed}) {t.name}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                      {/* Region 2 */}
                      <div className="border border-gray-200 rounded-md p-4">
                        <Label className="font-manrope font-semibold mb-2 block">To Region</Label>
                        <select
                          data-testid="swap-to-region"
                          value={swapRegion2}
                          onChange={(e) => { setSwapRegion2(e.target.value); setSwapTeamsR2([]); }}
                          className="w-full p-2 border border-gray-300 rounded-md font-manrope text-sm mb-3"
                        >
                          <option value="">Select region...</option>
                          {['East', 'West', 'South', 'Midwest'].filter(r => r !== swapRegion1).map(r => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                        </select>
                        {swapRegion2 && (
                          <div className="space-y-1 max-h-48 overflow-y-auto">
                            {[...teams].filter(t => t.region === swapRegion2).sort((a, b) => a.seed - b.seed).map(t => (
                              <label key={t.team_id} className="flex items-center gap-2 p-1.5 rounded hover:bg-gray-50 cursor-pointer font-manrope text-sm">
                                <input
                                  type="checkbox"
                                  checked={swapTeamsR2.includes(t.team_id)}
                                  onChange={(e) => {
                                    if (e.target.checked) setSwapTeamsR2(prev => [...prev, t.team_id]);
                                    else setSwapTeamsR2(prev => prev.filter(id => id !== t.team_id));
                                  }}
                                  className="rounded"
                                />
                                ({t.seed}) {t.name}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    {swapTeamsR1.length > 0 && swapTeamsR2.length > 0 && (
                      <div className="bg-amber-50 border border-amber-200 p-3 rounded-md">
                        <p className="font-manrope text-sm text-amber-800">
                          <strong>Preview:</strong>{' '}
                          {swapTeamsR1.map(id => teams.find(t => t.team_id === id)?.name).join(', ')}
                          {` (${swapRegion1} → ${swapRegion2})`}
                          {' ⇄ '}
                          {swapTeamsR2.map(id => teams.find(t => t.team_id === id)?.name).join(', ')}
                          {` (${swapRegion2} → ${swapRegion1})`}
                        </p>
                      </div>
                    )}
                    <Button
                      data-testid="swap-regions-button"
                      onClick={handleSwapRegions}
                      disabled={swapTeamsR1.length === 0 || swapTeamsR2.length === 0 || swapping}
                      className="btn-primary px-6 py-2"
                    >
                      {swapping ? 'Swapping...' : 'Swap Regions'}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Fix Bracket Positions */}
            {teams.length > 0 && (
              <div className="bg-white rounded-md card-shadow p-6 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-2xl">🔧</span>
                      <h2 className="font-bebas text-3xl text-emerald-900 tracking-wider">
                        Fix Bracket Positions
                      </h2>
                    </div>
                    <p className="font-manrope text-gray-600 text-sm">
                      Recomputes game display order for all regions based on seed matchups (1v16, 8v9, 5v12, etc.)
                    </p>
                  </div>
                  <Button
                    data-testid="fix-bracket-positions-button"
                    onClick={async () => {
                      try {
                        const res = await axios.post(`${API}/admin/fix-bracket-positions?tournament_id=${selectedTournamentId}`, {}, { withCredentials: true });
                        toast.success(res.data.message);
                        window.location.reload();
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to fix positions');
                      }
                    }}
                    className="btn-primary px-6 py-2"
                  >
                    Fix Positions
                  </Button>
                </div>
              </div>
            )}

            {/* Payment Settings */}
            <div className="bg-white rounded-md card-shadow p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <span className="text-2xl">💰</span>
                  <h2 className="font-bebas text-3xl text-emerald-900 tracking-wider">
                    Payment Settings
                  </h2>
                </div>
                <Button
                  data-testid="toggle-payment-form-button"
                  onClick={() => setShowPaymentForm(!showPaymentForm)}
                  className="btn-secondary px-4 py-2"
                >
                  {showPaymentForm ? 'Hide' : 'Edit'} Payment Info
                </Button>
              </div>

              {currentTournament.entry_fee || currentTournament.paypal_username || 
               currentTournament.venmo_username || currentTournament.cashapp_username ? (
                <div className="bg-emerald-50 p-4 rounded-md mb-4">
                  {currentTournament.entry_fee && (
                    <p className="font-manrope text-emerald-900 mb-2">
                      <strong>Entry Fee:</strong> ${currentTournament.entry_fee}
                    </p>
                  )}
                  {currentTournament.paypal_username && (
                    <p className="font-manrope text-emerald-900 mb-2">
                      <strong>PayPal:</strong> @{currentTournament.paypal_username}
                    </p>
                  )}
                  {currentTournament.venmo_username && (
                    <p className="font-manrope text-emerald-900 mb-2">
                      <strong>Venmo:</strong> @{currentTournament.venmo_username}
                    </p>
                  )}
                  {currentTournament.cashapp_username && (
                    <p className="font-manrope text-emerald-900">
                      <strong>Cash App:</strong> ${currentTournament.cashapp_username}
                    </p>
                  )}
                </div>
              ) : (
                <p className="font-manrope text-gray-600 mb-4">No payment info set yet</p>
              )}

              {showPaymentForm && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="entry-fee" className="font-manrope">Entry Fee ($)</Label>
                    <Input
                      id="entry-fee"
                      data-testid="entry-fee-input"
                      type="number"
                      step="0.01"
                      placeholder="20.00"
                      value={entryFee}
                      onChange={(e) => setEntryFee(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="paypal" className="font-manrope">PayPal Username</Label>
                    <Input
                      id="paypal"
                      data-testid="paypal-input"
                      type="text"
                      placeholder="your.email@example.com"
                      value={paypalUsername}
                      onChange={(e) => setPaypalUsername(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="venmo" className="font-manrope">Venmo Username</Label>
                    <Input
                      id="venmo"
                      data-testid="venmo-input"
                      type="text"
                      placeholder="@YourUsername"
                      value={venmoUsername}
                      onChange={(e) => setVenmoUsername(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cashapp" className="font-manrope">Cash App Username</Label>
                    <Input
                      id="cashapp"
                      data-testid="cashapp-input"
                      type="text"
                      placeholder="YourCashtag"
                      value={cashappUsername}
                      onChange={(e) => setCashappUsername(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <Button
                    data-testid="save-payment-button"
                    onClick={handleUpdatePaymentInfo}
                    className="btn-primary px-6 py-2"
                  >
                    Save Payment Info
                  </Button>
                </div>
              )}
            </div>

            {/* Participants & Payment Tracking */}
            <div className="bg-white rounded-md card-shadow p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <span className="text-2xl">✅</span>
                  <h2 className="font-bebas text-3xl text-emerald-900 tracking-wider">
                    Payment Tracking
                  </h2>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex gap-3 text-sm font-manrope font-semibold">
                    <span className="text-green-700">
                      ✓ {participants.filter(p => p.paid).length} Paid
                    </span>
                    <span className="text-amber-700">
                      ⏳ {participants.filter(p => p.payment_pending && !p.paid).length} Pending
                    </span>
                    <span className="text-red-700">
                      ⚠️ {participants.filter(p => !p.paid && !p.payment_pending).length} Unpaid
                    </span>
                  </div>
                  <Button
                    onClick={() => setShowParticipants(!showParticipants)}
                    className="btn-secondary px-4 py-2"
                  >
                    {showParticipants ? 'Hide' : 'Show'} Participants
                  </Button>
                </div>
              </div>

              {showParticipants && (
                <div className="space-y-2">
                  {participants.length === 0 ? (
                    <p className="font-manrope text-gray-600">No participants yet</p>
                  ) : (
                    participants.map((participant) => {
                      // Determine payment status
                      const isPaid = participant.paid === true;
                      const isPending = participant.payment_pending === true && !isPaid;
                      const isUnpaid = !isPaid && !isPending;
                      
                      return (
                        <div
                          key={participant.participant_id}
                          className={`flex items-center justify-between p-4 rounded-md border ${
                            isPaid 
                              ? 'bg-green-50 border-green-500' 
                              : isPending 
                              ? 'bg-amber-50 border-amber-500' 
                              : 'bg-gray-50 border-gray-300'
                          }`}
                        >
                          <div className="flex-1">
                            <div className="font-manrope font-bold text-emerald-900">
                              {participant.user_name}
                              {isPaid && (
                                <span className="ml-2 text-xs bg-green-600 text-white px-2 py-1 rounded">
                                  ✓ PAID
                                </span>
                              )}
                              {isPending && (
                                <span className="ml-2 text-xs bg-amber-600 text-white px-2 py-1 rounded">
                                  ⏳ PENDING
                                </span>
                              )}
                              {isUnpaid && (
                                <span className="ml-2 text-xs bg-red-600 text-white px-2 py-1 rounded">
                                  ⚠️ UNPAID
                                </span>
                              )}
                            </div>
                            <div className="font-manrope text-sm text-gray-600">
                              {participant.user_email}
                            </div>
                            {participant.payment_method && (
                              <div className="font-manrope text-xs text-gray-500 mt-1">
                                Payment Method: <span className="font-semibold">{participant.payment_method}</span>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex gap-2">
                            {isPending && (
                              <Button
                                onClick={() => handleConfirmPayment(participant.user_id)}
                                className="btn-secondary px-4 py-2 text-sm"
                              >
                                Confirm Payment
                              </Button>
                            )}
                            {isPaid && (
                              <Button
                                onClick={() => handleUnconfirmPayment(participant.user_id)}
                                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 text-sm"
                              >
                                Mark Unpaid
                              </Button>
                            )}
                            {isUnpaid && (
                              <Button
                                onClick={() => handleConfirmPayment(participant.user_id)}
                                className="btn-secondary px-4 py-2 text-sm"
                              >
                                Mark Paid
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            {/* Lock Picks */}
            <div className="bg-white rounded-md card-shadow p-6 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-bebas text-3xl text-emerald-900 tracking-wider mb-2">
                    Lock Picks
                  </h2>
                  <p className="font-manrope text-gray-600">
                    {currentTournament.picks_locked 
                      ? '🔒 Picks are currently LOCKED - users cannot change their selections'
                      : '🔓 Picks are UNLOCKED - users can still edit their selections'}
                  </p>
                </div>
                <Button
                  onClick={() => handleLockPicks(selectedTournamentId, !currentTournament.picks_locked)}
                  className={`px-6 py-3 ${
                    currentTournament.picks_locked ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {currentTournament.picks_locked ? 'Unlock Picks' : 'Lock Picks'}
                </Button>
              </div>
            </div>

            {/* Game Management */}
            <div className="bg-white rounded-md card-shadow p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <span className="text-2xl">🏀</span>
                  <h2 className="font-bebas text-3xl text-emerald-900 tracking-wider">
                    Manage Games ({games.filter(g => g.completed).length}/{games.length} Complete)
                  </h2>
                </div>
                <div className="flex space-x-2">
                  <Button
                    onClick={handleGenerateNextRound}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2"
                  >
                    Generate Next Round
                  </Button>
                  <Button
                    onClick={() => setShowGames(!showGames)}
                    className="btn-secondary px-4 py-2"
                  >
                    {showGames ? 'Hide' : 'Show'} Games
                  </Button>
                </div>
              </div>

              {showGames && (
                <div className="space-y-4">
                  {/* Round Filter */}
                  <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-md border border-gray-300">
                    <Label className="font-manrope font-bold text-emerald-900">Filter by Round:</Label>
                    <select
                      value={selectedRound}
                      onChange={(e) => setSelectedRound(e.target.value)}
                      className="border border-gray-300 rounded px-4 py-2 font-manrope"
                    >
                      <option value="all">All Rounds</option>
                      {[...new Set(games.map(g => g.round))].sort().map((round) => {
                        const roundLabel = round === 1 ? 'Round 1 - Round of 64' 
                          : round === 2 ? 'Round 2 - Round of 32' 
                          : round === 3 ? 'Round 3 - Sweet 16'
                          : round === 4 ? 'Round 4 - Elite 8'
                          : round === 5 ? 'Round 5 - Final Four'
                          : round === 6 ? 'Round 6 - Championship'
                          : `Round ${round}`;
                        return <option key={round} value={round}>{roundLabel}</option>;
                      })}
                    </select>
                  </div>

                  {/* Games by Round */}
                  <div className="space-y-6">
                  {[...new Set(games.map(g => g.round))].sort()
                    .filter(round => selectedRound === 'all' || round === parseInt(selectedRound))
                    .map((round) => {
                    const roundGames = games.filter(g => g.round === round);
                    const roundLabel = round === 1 ? 'Round of 64' 
                      : round === 2 ? 'Round of 32' 
                      : round === 3 ? 'Sweet 16'
                      : round === 4 ? 'Elite 8'
                      : round === 5 ? 'Final Four'
                      : round === 6 ? 'Championship'
                      : `Round ${round}`;
                    
                    return (
                      <div key={round} className="border-2 border-emerald-900 rounded-md p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-bebas text-3xl text-emerald-900">
                            {roundLabel} ({roundGames.filter(g => g.completed).length}/{roundGames.length} Complete)
                          </h3>
                          {user?.is_superadmin && (
                            <div className="flex space-x-2">
                              <Button
                                onClick={() => handleRegenerateRound(round)}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 text-sm rounded"
                              >
                                Regenerate
                              </Button>
                              <Button
                                onClick={() => handleDeleteRound(round)}
                                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 text-sm rounded"
                              >
                                Delete
                              </Button>
                            </div>
                          )}
                        </div>
                        
                        <div className="space-y-4">
                          {(round <= 4 ? ['East', 'West', 'South', 'Midwest'] : ['Final Four', 'Championship']).map((region) => {
                            const regionGames = roundGames.filter(g => g.region === region);
                            if (regionGames.length === 0) return null;
                            
                            return (
                              <div key={region}>
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="font-bebas text-xl text-amber-600">{region}</h4>
                                  {round >= 3 && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-xs text-red-600 border-red-300 hover:bg-red-50"
                                      data-testid={`purge-${region}-r${round}`}
                                      onClick={() => handlePurgeRoundRegion(round, region)}
                                    >
                                      Purge R{round} {region}
                                    </Button>
                                  )}
                                </div>
                                <div className="space-y-2">
                                  {[...regionGames].sort((a, b) => a.bracket_position - b.bracket_position).map((game) => {
                                    const team1 = teams.find(t => t.team_id === game.team1_id);
                                    const team2 = teams.find(t => t.team_id === game.team2_id);
                                    
                                    if (!team1 || !team2) return null;
                                    
                                    return (
                                      <div
                                        key={game.game_id}
                                        className={`p-4 rounded-md border ${
                                          game.completed ? 'bg-green-50 border-green-500' : 'bg-gray-50 border-gray-300'
                                        }`}
                                      >
                                        <div className="flex items-center justify-between">
                                          <div className="flex-1">
                                            <div className="font-manrope text-sm text-gray-600 mb-2">
                                              Round {game.round}
                                              {game.completed && (
                                                <span className="ml-2 text-xs bg-green-600 text-white px-2 py-1 rounded">
                                                  COMPLETE
                                                </span>
                                              )}
                                            </div>
                                            <div className="flex items-center space-x-4">
                                              <div className={`flex-1 p-3 rounded ${
                                                game.winner_id === team1.team_id ? 'bg-amber-100 border-2 border-amber-600' : 'bg-white'
                                              }`}>
                                                <div className="font-bebas text-lg text-emerald-900">
                                                  ({team1.seed}) {team1.name}
                                                </div>
                                              </div>
                                              <span className="font-bebas text-xl text-gray-400">VS</span>
                                              <div className={`flex-1 p-3 rounded ${
                                                game.winner_id === team2.team_id ? 'bg-amber-100 border-2 border-amber-600' : 'bg-white'
                                              }`}>
                                                <div className="font-bebas text-lg text-emerald-900">
                                                  ({team2.seed}) {team2.name}
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                          {!game.completed && (
                                            <div className="ml-4 flex flex-col space-y-2">
                                              <Button
                                                onClick={() => handleCompleteGame(game.game_id, team1.team_id)}
                                                className="btn-secondary px-4 py-2 text-sm"
                                              >
                                                {team1.name} Wins
                                              </Button>
                                              <Button
                                                onClick={() => handleCompleteGame(game.game_id, team2.team_id)}
                                                className="btn-secondary px-4 py-2 text-sm"
                                              >
                                                {team2.name} Wins
                                              </Button>
                                            </div>
                                          )}
                                          {game.completed && (
                                            <div className="ml-4 flex flex-col space-y-2">
                                              <div className="text-sm font-manrope text-green-600 font-bold mb-2">
                                                Winner: {game.winner_id === team1.team_id ? team1.name : team2.name}
                                              </div>
                                              <Button
                                                onClick={() => handleCompleteGame(game.game_id, game.winner_id === team1.team_id ? team2.team_id : team1.team_id)}
                                                className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 text-sm rounded"
                                              >
                                                Change Winner
                                              </Button>
                                              <Button
                                                onClick={() => handleResetGame(game.game_id)}
                                                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 text-sm rounded"
                                              >
                                                Reset Game
                                              </Button>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Tournament History */}
        <div className="bg-white rounded-md card-shadow p-6 mb-6">
          <div className="flex items-center space-x-2 mb-4">
            <Award className="text-amber-600" size={24} />
            <h2 className="font-bebas text-3xl text-emerald-900 tracking-wider">
              Tournament History
            </h2>
          </div>
          <div className="space-y-3">
            {tournaments.map((tournament) => (
              <div
                key={tournament.tournament_id}
                data-testid={`tournament-${tournament.tournament_id}`}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-md border border-emerald-900"
              >
                <div className="flex-1">
                  <div className="font-bebas text-2xl text-emerald-900">
                    {tournament.name || `${tournament.year} Tournament`}
                  </div>
                  <div className="font-manrope text-sm text-gray-600 mb-2">
                    Year: {tournament.year} | Status: <span className="font-bold uppercase">{tournament.status}</span>
                  </div>
                  
                  {/* Semifinal Configuration Display */}
                  {tournament.semi1_region1 && tournament.semi2_region1 && editingConfigTournamentId !== tournament.tournament_id && (
                    <div className="bg-white p-3 rounded border border-emerald-300 mt-2">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-manrope text-xs font-bold text-emerald-900">Final Four Configuration:</div>
                        {user?.is_superadmin && (
                          <Button
                            onClick={() => handleEditConfig(tournament)}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 text-xs rounded"
                          >
                            Edit
                          </Button>
                        )}
                      </div>
                      <div className="space-y-1 text-xs font-manrope">
                        <div className="flex items-center">
                          <span className="text-amber-700 font-semibold w-20">Semi 1:</span>
                          <span className="text-gray-700">{tournament.semi1_region1} vs {tournament.semi1_region2}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-amber-700 font-semibold w-20">Semi 2:</span>
                          <span className="text-gray-700">{tournament.semi2_region1} vs {tournament.semi2_region2}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Semifinal Configuration Edit Mode */}
                  {editingConfigTournamentId === tournament.tournament_id && (
                    <div className="bg-amber-50 p-4 rounded border-2 border-amber-500 mt-2">
                      <div className="font-manrope text-sm font-bold text-emerald-900 mb-3">Edit Final Four Configuration:</div>
                      
                      <div className="space-y-3 mb-3">
                        <div>
                          <Label className="font-manrope text-xs font-bold text-emerald-900 mb-1 block">Semifinal 1</Label>
                          <div className="flex items-center space-x-2">
                            <select
                              value={editSemi1Region1}
                              onChange={(e) => setEditSemi1Region1(e.target.value)}
                              className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded-md font-manrope"
                            >
                              <option value="East">East</option>
                              <option value="West">West</option>
                              <option value="South">South</option>
                              <option value="Midwest">Midwest</option>
                            </select>
                            <span className="font-bebas text-lg text-gray-500">vs</span>
                            <select
                              value={editSemi1Region2}
                              onChange={(e) => setEditSemi1Region2(e.target.value)}
                              className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded-md font-manrope"
                            >
                              <option value="East">East</option>
                              <option value="West">West</option>
                              <option value="South">South</option>
                              <option value="Midwest">Midwest</option>
                            </select>
                          </div>
                        </div>
                        
                        <div>
                          <Label className="font-manrope text-xs font-bold text-emerald-900 mb-1 block">Semifinal 2</Label>
                          <div className="flex items-center space-x-2">
                            <select
                              value={editSemi2Region1}
                              onChange={(e) => setEditSemi2Region1(e.target.value)}
                              className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded-md font-manrope"
                            >
                              <option value="East">East</option>
                              <option value="West">West</option>
                              <option value="South">South</option>
                              <option value="Midwest">Midwest</option>
                            </select>
                            <span className="font-bebas text-lg text-gray-500">vs</span>
                            <select
                              value={editSemi2Region2}
                              onChange={(e) => setEditSemi2Region2(e.target.value)}
                              className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded-md font-manrope"
                            >
                              <option value="East">East</option>
                              <option value="West">West</option>
                              <option value="South">South</option>
                              <option value="Midwest">Midwest</option>
                            </select>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex space-x-2">
                        <Button
                          onClick={() => handleSaveConfig(tournament.tournament_id)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1 text-sm rounded"
                        >
                          Save
                        </Button>
                        <Button
                          onClick={handleCancelEditConfig}
                          className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 text-sm rounded"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex space-x-2">
                  {tournament.status === 'active' && user?.is_superadmin && (
                    <Button
                      data-testid={`status-completed-${tournament.tournament_id}`}
                      onClick={() => handleUpdateStatus(tournament.tournament_id, 'completed')}
                      className="btn-primary px-4 py-2 text-sm"
                    >
                      Complete
                    </Button>
                  )}
                  {tournament.status === 'completed' && user?.is_superadmin && (
                    <>
                      <Button
                        onClick={() => handleUpdateStatus(tournament.tournament_id, 'active')}
                        className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 text-sm rounded"
                      >
                        ← Reopen (Active)
                      </Button>
                      <Button
                        onClick={() => handleUpdateStatus(tournament.tournament_id, 'archived')}
                        className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 text-sm rounded"
                      >
                        Archive
                      </Button>
                    </>
                  )}
                  {tournament.status === 'archived' && user?.is_superadmin && (
                    <Button
                      onClick={() => handleUpdateStatus(tournament.tournament_id, 'active')}
                      className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 text-sm rounded"
                    >
                      ← Unarchive (Active)
                    </Button>
                  )}
                  {user?.is_superadmin && (
                    <Button
                      onClick={() => handleDeleteTournament(tournament.tournament_id)}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm rounded"
                    >
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Email Broadcast */}
        <div className="bg-white rounded-md card-shadow p-6 mb-6">
          <div className="flex items-center space-x-2 mb-4">
            <Mail className="text-amber-600" size={24} />
            <h2 className="font-bebas text-3xl text-emerald-900 tracking-wider">
              Email Broadcast
            </h2>
          </div>
          
          {/* Success Confirmation Banner */}
          {emailSentConfirmation && (
            <div className="mb-4 p-4 bg-green-100 border-2 border-green-600 rounded-md">
              <div className="flex items-center gap-3">
                <span className="text-3xl">✅</span>
                <div className="font-manrope">
                  <p className="font-bold text-green-900 text-lg">
                    Email Successfully Sent!
                  </p>
                  <p className="text-green-800">{emailSentConfirmation}</p>
                </div>
              </div>
            </div>
          )}
          
          <p className="font-manrope text-sm text-gray-600 mb-4">
            Send emails to users based on selected filter
          </p>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="email-filter" className="font-manrope">
                Recipients Filter
              </Label>
              <select
                id="email-filter"
                value={emailFilter}
                onChange={(e) => setEmailFilter(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 font-manrope"
                disabled={emailTestMode}
              >
                <option value="all">All Users</option>
                <option value="participants">Tournament Participants</option>
                <option value="paid">Paid Participants</option>
                <option value="unpaid">Unpaid Participants</option>
                <option value="with_picks">Users With Picks</option>
                <option value="without_picks">Users Without Picks</option>
              </select>
            </div>

            {/* Test Mode Toggle */}
            <div className="p-4 bg-amber-50 border-2 border-amber-600 rounded-md">
              <div className="flex items-center space-x-3 mb-2">
                <input
                  type="checkbox"
                  id="test-mode"
                  checked={emailTestMode}
                  onChange={(e) => setEmailTestMode(e.target.checked)}
                  className="w-5 h-5"
                />
                <Label htmlFor="test-mode" className="font-manrope font-bold text-emerald-900 cursor-pointer">
                  🧪 Test Mode (Send to specific emails only)
                </Label>
              </div>
              {emailTestMode && (
                <div className="mt-3">
                  <Label htmlFor="test-emails" className="font-manrope text-sm">
                    Test Email Addresses (comma-separated)
                  </Label>
                  <Input
                    id="test-emails"
                    type="text"
                    placeholder="email1@example.com, email2@example.com"
                    value={testEmails}
                    onChange={(e) => setTestEmails(e.target.value)}
                    className="font-manrope mt-1"
                  />
                  <p className="text-xs text-gray-600 mt-1 font-manrope">
                    Email will only be sent to these addresses, ignoring the filter above
                  </p>
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="email-subject" className="font-manrope">
                Email Subject
              </Label>
              <Input
                id="email-subject"
                type="text"
                placeholder="e.g., Tournament Update"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                className="font-manrope"
              />
            </div>

            <div>
              <Label htmlFor="email-content" className="font-manrope mb-2 block">
                Email Content
              </Label>
              <SimpleRichEditor
                value={emailContent}
                onChange={(html) => setEmailContent(html)}
                placeholder="Type your message..."
              />
            </div>

            <Button
              onClick={handleSendBroadcastEmail}
              disabled={sendingEmail || !selectedTournamentId}
              className="btn-primary w-full"
            >
              {sendingEmail ? 'Sending...' : 'Send Email'}
            </Button>
          </div>
        </div>

        {/* Email Logs */}
        <div className="bg-white rounded-md card-shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <FileText className="text-amber-600" size={24} />
              <h2 className="font-bebas text-3xl text-emerald-900 tracking-wider">
                Email Logs
              </h2>
            </div>
            <Button
              data-testid="toggle-email-logs"
              onClick={() => { setShowEmailLogs(!showEmailLogs); if (!showEmailLogs && emailLogs.length === 0) fetchEmailLogs(); }}
              className="btn-secondary px-4 py-2 text-sm"
            >
              {showEmailLogs ? 'Hide Logs' : 'View Logs'}
            </Button>
          </div>
          {showEmailLogs && (
            <div className="space-y-3">
              {loadingEmailLogs ? (
                <p className="font-manrope text-gray-500 text-center py-4">Loading logs...</p>
              ) : emailLogs.length === 0 ? (
                <p className="font-manrope text-gray-500 text-center py-4">No email logs yet.</p>
              ) : (
                <>
                  <Button
                    onClick={fetchEmailLogs}
                    className="btn-secondary px-3 py-1 text-xs mb-2"
                  >
                    Refresh
                  </Button>
                  {emailLogs.map((log) => (
                    <div key={log.broadcast_id} className="border border-gray-200 rounded-md overflow-hidden">
                      <div
                        className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer hover:bg-gray-100"
                        onClick={(e) => {
                          const detail = e.currentTarget.nextElementSibling;
                          detail.style.display = detail.style.display === 'none' ? 'block' : 'none';
                        }}
                      >
                        <div>
                          <div className="font-manrope font-bold text-emerald-900">{log.subject}</div>
                          <div className="font-manrope text-xs text-gray-500">
                            {new Date(log.sent_at).toLocaleString()} &middot; {log.filter_type}{log.test_mode ? ' (TEST)' : ''}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-manrope text-sm font-bold text-emerald-700">{log.sent_count} sent</span>
                          {log.failed_count > 0 && (
                            <span className="font-manrope text-sm font-bold text-red-600">{log.failed_count} failed</span>
                          )}
                          <span className="font-manrope text-xs text-gray-400">click to expand</span>
                        </div>
                      </div>
                      <div style={{ display: 'none' }} className="p-4 border-t border-gray-200">
                        <table className="w-full text-sm font-manrope">
                          <thead>
                            <tr className="text-left text-gray-500 border-b">
                              <th className="pb-2 pr-4">Recipient</th>
                              <th className="pb-2">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {log.recipients?.map((r, i) => (
                              <tr key={i} className="border-b border-gray-100">
                                <td className="py-2 pr-4">{r.email}</td>
                                <td className="py-2">
                                  <span className={`px-2 py-1 rounded text-xs font-bold ${r.status === 'sent' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                    {r.status}
                                  </span>
                                  {r.error && <span className="text-xs text-red-500 ml-2">{r.error}</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>

        {/* User Management */}
        <div className="bg-white rounded-md card-shadow p-6">
          <div className="flex items-center space-x-2 mb-4">
            <UserCog className="text-amber-600" size={24} />
            <h2 className="font-bebas text-3xl text-emerald-900 tracking-wider">
              User Management
            </h2>
          </div>
          <div className="space-y-2">
            {allUsers.map((u) => (
              <div
                key={u.user_id}
                data-testid={`user-${u.user_id}`}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-md border border-gray-300"
              >
                <div className="flex items-center space-x-3 flex-1">
                  {u.picture && (
                    <img
                      src={u.picture}
                      alt={u.name}
                      className="w-10 h-10 rounded-full border-2 border-emerald-900"
                    />
                  )}
                  <div className="flex-1">
                    {editingUserId === u.user_id ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={editingUserName}
                          onChange={(e) => setEditingUserName(e.target.value)}
                          className="px-3 py-1 border border-emerald-500 rounded-md font-manrope font-bold"
                          autoFocus
                        />
                        <Button
                          onClick={() => handleSaveUserName(u.user_id)}
                          className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 text-sm"
                        >
                          Save
                        </Button>
                        <Button
                          onClick={handleCancelEditUserName}
                          className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 text-sm"
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <div className="font-manrope font-bold text-emerald-900 flex items-center space-x-2">
                        <span>{u.name}</span>
                        <button
                          onClick={() => handleEditUserName(u.user_id, u.name)}
                          className="text-blue-600 hover:text-blue-800 text-xs"
                          title="Edit name"
                        >
                          ✏️ Edit
                        </button>
                        {u.is_superadmin && (
                          <span className="text-xs bg-emerald-600 text-white px-2 py-1 rounded font-semibold">
                            SUPERADMIN
                          </span>
                        )}
                        {u.is_admin && !u.is_superadmin && (
                          <span className="text-xs bg-amber-600 text-white px-2 py-1 rounded">
                            ADMIN
                          </span>
                        )}
                      </div>
                    )}
                    <div className="font-manrope text-sm text-gray-600">{u.email}</div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {u.is_superadmin ? (
                    // SuperAdmin can be demoted (if not current user)
                    user?.is_superadmin && user?.user_id !== u.user_id ? (
                      <Button
                        onClick={() => handleDemoteSuperAdmin(u.user_id)}
                        className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 text-sm rounded"
                      >
                        Remove SuperAdmin
                      </Button>
                    ) : (
                      <span className="text-sm text-gray-500 italic">SuperAdmin</span>
                    )
                  ) : u.is_admin ? (
                    // Regular admin can be promoted to SuperAdmin or demoted
                    <div className="flex items-center space-x-2">
                      {user?.is_superadmin && (
                        <Button
                          onClick={() => handlePromoteSuperAdmin(u.user_id)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 text-sm rounded"
                        >
                          Make SuperAdmin
                        </Button>
                      )}
                      <Button
                        onClick={() => handleDemoteUser(u.user_id)}
                        className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 text-sm rounded"
                      >
                        Remove Admin
                      </Button>
                    </div>
                  ) : (
                    // Regular user can be promoted to admin or deleted
                    <>
                      <Button
                        data-testid={`promote-${u.user_id}`}
                        onClick={() => handlePromoteUser(u.user_id)}
                        className="btn-secondary px-4 py-2 text-sm"
                      >
                        Make Admin
                      </Button>
                      <Button
                        onClick={() => handleDeleteUser(u.user_id, u.name)}
                        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm rounded"
                      >
                        Delete
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
